import * as React from "react";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { BottomSheet } from "./mobile/BottomSheet.js";

/**
 * A single selectable option. `secondary` renders as muted trailing text in the
 * dropdown row (e.g. a test's scope) and is also matched against the search
 * query.
 */
export interface MultiSelectOption {
  value: string;
  label: string;
  secondary?: string;
}

interface MultiSelectComboboxProps {
  id: string;
  options: MultiSelectOption[];
  /** Selected option values. The parent owns this state. */
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  /** Accessible label for the listbox; falls back to the placeholder. */
  ariaLabel?: string;
  /** Title shown on the mobile bottom-sheet picker. */
  sheetTitle?: string;
  /** When true, the user can add free-text entries that aren't in the list:
   * on desktop by pressing Enter, on mobile via the "Add …" row. An exact
   * label match still resolves to that option. */
  allowCustom?: boolean;
}

/**
 * Searchable multi-select.
 *
 * - **Desktop** (≥768px): an inline control of removable chips that doubles as
 *   the search input, with a floating filtered listbox. Keyboard: ↓/↑ move the
 *   active option, Enter toggles (or commits free text), Escape closes,
 *   Backspace on an empty query removes the last chip.
 * - **Mobile** (<768px): a tappable trigger that opens a full-width bottom
 *   sheet — a floating dropdown collides with the soft keyboard and is far too
 *   cramped for a long list on a phone. The sheet has a search field and large
 *   tappable rows.
 *
 * Purely presentational — selection state is owned by the parent.
 */
export function MultiSelectCombobox({
  id,
  options,
  value,
  onChange,
  placeholder = "Search…",
  ariaLabel,
  sheetTitle = "Select",
  allowCustom = false,
}: MultiSelectComboboxProps): React.ReactElement {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);

  const selectedSet = React.useMemo(() => new Set(value), [value]);

  // A label map keeps chip rendering O(1) and independent of filter state.
  const labelByValue = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(o.value, o.label);
    return m;
  }, [options]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.secondary ? o.secondary.toLowerCase().includes(q) : false),
    );
  }, [options, query]);

  // Keep the active row in range whenever the filtered set shrinks/grows.
  React.useEffect(() => {
    setActiveIndex((i) => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  // Desktop only: close on outside pointerdown so a selection inside the menu
  // isn't lost. The mobile sheet handles its own scrim/Escape close.
  React.useEffect(() => {
    if (isMobile || !open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, isMobile]);

  // Desktop only: scroll the active option into view as it moves.
  React.useEffect(() => {
    if (isMobile || !open || !listRef.current) return;
    const node = listRef.current.children[activeIndex] as HTMLElement | undefined;
    node?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, isMobile]);

  function toggle(optValue: string) {
    if (selectedSet.has(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  }

  function removeValue(optValue: string) {
    onChange(value.filter((v) => v !== optValue));
  }

  function removeLast() {
    if (value.length === 0) return;
    onChange(value.slice(0, -1));
  }

  // Commit the typed query: an exact (case-insensitive) label match selects
  // that option; otherwise the raw text becomes its own selection.
  function commitQuery() {
    const q = query.trim();
    if (q === "") return;
    const exact = options.find((o) => o.label.toLowerCase() === q.toLowerCase());
    const next = exact ? exact.value : q;
    if (!value.some((v) => v.toLowerCase() === next.toLowerCase())) {
      onChange([...value, next]);
    }
    setQuery("");
  }

  // Whether to surface a free-text "Add …" affordance for the current query.
  const trimmedQuery = query.trim();
  const showAddRow =
    allowCustom &&
    trimmedQuery !== "" &&
    !options.some((o) => o.label.toLowerCase() === trimmedQuery.toLowerCase()) &&
    !value.some((v) => v.toLowerCase() === trimmedQuery.toLowerCase());

  const listId = `${id}-listbox`;

  // ─── Mobile: trigger + bottom-sheet picker ─────────────────────────
  if (isMobile) {
    return (
      <div className="auth-gate__ms auth-gate__ms--mobile">
        {/* Chips live ABOVE the trigger (not inside it) so the trigger can be a
         * real <button> — labelable via htmlFor and free of nested buttons. */}
        {value.length > 0 && (
          <div className="auth-gate__ms-chips">
            {value.map((v) => (
              <span key={v} className="auth-gate__ms-chip">
                {labelByValue.get(v) ?? v}
                <button
                  type="button"
                  className="auth-gate__ms-chip-x"
                  aria-label={`Remove ${labelByValue.get(v) ?? v}`}
                  onClick={() => removeValue(v)}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="18" y1="6" x2="6" y2="18" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        )}
        <button
          type="button"
          id={id}
          className="auth-gate__ms-trigger"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={ariaLabel ?? placeholder}
          onClick={() => setOpen(true)}
        >
          <span className="auth-gate__ms-trigger-ph">
            {value.length === 0 ? placeholder : "Add another test"}
          </span>
          <span className="auth-gate__ms-trigger-add" aria-hidden="true">
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </span>
        </button>
        <BottomSheet
          open={open}
          onClose={() => {
            setOpen(false);
            setQuery("");
          }}
          title={sheetTitle}
          className="auth-gate__ms-sheet"
        >
          <div className="auth-gate__ms-sheet-search">
            <input
              id={`${id}-search`}
              className="auth-gate__input"
              type="text"
              autoComplete="off"
              aria-label={ariaLabel ?? sheetTitle}
              placeholder={placeholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && allowCustom && trimmedQuery !== "") {
                  e.preventDefault();
                  commitQuery();
                }
              }}
            />
          </div>
          <ul
            className="auth-gate__ms-sheet-list"
            role="listbox"
            aria-multiselectable="true"
            aria-label={ariaLabel ?? sheetTitle}
          >
            {showAddRow && (
              <li
                role="option"
                aria-selected={false}
                className="auth-gate__ms-sheet-row auth-gate__ms-sheet-add"
                onClick={commitQuery}
              >
                <span className="auth-gate__ms-sheet-add-icon" aria-hidden="true">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  >
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </span>
                Add &ldquo;{trimmedQuery}&rdquo;
              </li>
            )}
            {filtered.map((o) => {
              const isSelected = selectedSet.has(o.value);
              return (
                <li
                  key={o.value}
                  role="option"
                  aria-selected={isSelected}
                  className={`auth-gate__ms-sheet-row ${isSelected ? "is-selected" : ""}`}
                  onClick={() => toggle(o.value)}
                >
                  <span className="auth-gate__ms-check" aria-hidden="true">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="auth-gate__ms-opt-label">{o.label}</span>
                  {o.secondary && <span className="auth-gate__ms-opt-sec">{o.secondary}</span>}
                </li>
              );
            })}
            {filtered.length === 0 && !showAddRow && (
              <li className="auth-gate__ms-empty" role="presentation">
                No matches
              </li>
            )}
          </ul>
          <button
            type="button"
            className="auth-gate__submit auth-gate__ms-sheet-done"
            onClick={() => {
              setOpen(false);
              setQuery("");
            }}
          >
            Done
          </button>
        </BottomSheet>
      </div>
    );
  }

  // ─── Desktop: inline control + floating listbox ────────────────────
  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      setActiveIndex((i) => (filtered.length === 0 ? 0 : (i + 1) % filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) return;
      setActiveIndex((i) =>
        filtered.length === 0 ? 0 : (i - 1 + filtered.length) % filtered.length,
      );
    } else if (e.key === "Enter") {
      if (allowCustom && trimmedQuery !== "") {
        // Free-text commit takes precedence over list highlight when typing.
        e.preventDefault();
        commitQuery();
      } else if (open && filtered[activeIndex]) {
        e.preventDefault();
        toggle(filtered[activeIndex].value);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    } else if (e.key === "Backspace" && query === "") {
      removeLast();
    }
  }

  return (
    <div className="auth-gate__ms" ref={rootRef}>
      <div
        className={`auth-gate__ms-control ${open ? "is-open" : ""}`}
        onClick={() => {
          setOpen(true);
          inputRef.current?.focus();
        }}
      >
        {value.map((v) => (
          <span key={v} className="auth-gate__ms-chip">
            {labelByValue.get(v) ?? v}
            <button
              type="button"
              className="auth-gate__ms-chip-x"
              aria-label={`Remove ${labelByValue.get(v) ?? v}`}
              onClick={(e) => {
                e.stopPropagation();
                removeValue(v);
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3.5"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          className="auth-gate__ms-input"
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[activeIndex] ? `${id}-opt-${activeIndex}` : undefined
          }
          aria-label={ariaLabel ?? placeholder}
          autoComplete="off"
          placeholder={value.length === 0 ? placeholder : ""}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>
      {open && (
        <ul
          className="auth-gate__ms-menu"
          id={listId}
          role="listbox"
          aria-multiselectable="true"
          ref={listRef}
        >
          {filtered.length === 0 ? (
            <li className="auth-gate__ms-empty" role="presentation">
              No matches
            </li>
          ) : (
            filtered.map((o, i) => {
              const isSelected = selectedSet.has(o.value);
              return (
                <li
                  key={o.value}
                  id={`${id}-opt-${i}`}
                  role="option"
                  aria-selected={isSelected}
                  className={`auth-gate__ms-opt ${i === activeIndex ? "is-active" : ""} ${
                    isSelected ? "is-selected" : ""
                  }`}
                  onMouseEnter={() => setActiveIndex(i)}
                  // onMouseDown (not onClick) so the control's input keeps focus
                  // and the outside-pointerdown handler doesn't fire first.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    toggle(o.value);
                    // Picking a canonical option clears any pending free text so
                    // the selected value replaces what they were typing.
                    setQuery("");
                  }}
                >
                  <span className="auth-gate__ms-check" aria-hidden="true">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="auth-gate__ms-opt-label">{o.label}</span>
                  {o.secondary && <span className="auth-gate__ms-opt-sec">{o.secondary}</span>}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
